import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { ReactCalendarGroupRendererProps, ReactCalendarItemRendererProps, TimelineGroupBase } from "react-calendar-timeline";

import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import moment from "moment";

import {
  ModalProvider,
  areFieldOutcomesValidForYear,
  calculateAvgPayoutForYear,
  getFarmIdFromLocalStorage,
  isFieldEnrolled,
  isNextYearOutcomeValid,
  shouldShowNextYearCropsFeatureFlag,
  timeIsWithinReportingPeriod,
  useAppType,
  useGetCarbonProgramByFarmId,
  useGetFarms,
  useInView,
  useLocation,
  useNavigate,
  useTranslation,
} from "utils";

import { GET_FIELD_PRACTICES } from "consts";

import {
  ACCOUNTING_METHODOLOGY,
  CROP_TYPE_FCPO,
  CarbonProgram,
  CarbonProgramOutcomesResponse,
  CarbonProgramResult,
  CategorizedFieldCropRecommendation,
  CropFCPO,
  CropItemData,
  FieldCropRecommendationsResponse,
  FieldListField,
  FieldPracticeResponse,
  GetVerificationEditingRequestsResponse,
  ItemsWithMissingData,
  ItemsWithMissingDataByFieldId,
  PRACTICE_SLUG,
  PracticeFCPO,
  SingleFieldOutcomes,
  VIEW_TYPE,
  VerificationEditingRequest,
} from "models";

import { Button, CropRotationCard, FieldCell, Flex, Icon, MultiCropSelectionBar, Spin, TIMELINE_VIEW, TimelineCalendarWithFields } from "components";

import { AddEditCropDrawer } from "pages/app/all-field-view/components/AddEditCropDrawer";
import { BulkEditCrop, BulkEditCropDrawer } from "pages/app/all-field-view/components/BulkEditCropDrawer";
// import { moveResizeValidator } from "pages/app/field-timeline/utils/TimelineUtils";
import { NEW_CROP } from "pages/app/all-field-view/constants/cropForm";
import { useMissingDataNavigation } from "pages/app/all-field-view/hooks/useMissingDataNavigation";
import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";
import {
  getCropOpenTasksCount,
  getFieldOpenTasksCount,
  getItemsWithMissingDataSortedByStartTime,
  hasFieldLevelVerificationEditingRequests,
  hasItemsWithMissingData,
  isCheckboxClick,
} from "pages/app/all-field-view/utils/AllFieldsUtils.ts";
import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider";
import { SimulationFieldSelectionModal } from "pages/app/field-timeline-simulations/components/SimulationFieldSelectionModal";
import { PRACTICE_TYPE, TimelineCalendarItemsProps } from "pages/app/field-timeline/components/FieldDetailsTimeline.tsx";

type AllFieldsTimelineProps = {
  allFields: FieldListField[];
  filteredFields: FieldListField[];
  allFieldsPractices: Record<string, FieldPracticeResponse>;
  itemsWithMissingDataByFieldId: ItemsWithMissingDataByFieldId;
  timelineItems: TimelineCalendarItemsProps<CropItemData>[];
  carbonProgramOutcomesResponse?: CarbonProgramOutcomesResponse;
  setSimulationFieldId: Dispatch<SetStateAction<string | undefined>>;
  showSimulationFieldSelectionModal: boolean;
  setShowSimulations: Dispatch<SetStateAction<boolean>>;
  isLoadingCarbonProgramOutcomes?: boolean;
  fieldsInView: string[];
  setFieldsInView: Dispatch<SetStateAction<string[]>>;
  cropRecommendationsByFieldId: FieldCropRecommendationsResponse | undefined;
  farmFieldsCount: number;
  hasAppliedFilters: boolean;
  reportingYear: number;
  shouldShowIndividualFieldPayouts: boolean;
  verificationEditingRequests?: GetVerificationEditingRequestsResponse;
  handleClearFilters: () => void;
  onFieldNavigate?: () => void;
};

export enum UPDATE_TYPE {
  START = "START",
  END = "END",
  MOVE = "MOVE",
}

const dayInMilliseconds = 1 * 24 * 60 * 60 * 1000;
type EnrollmentAndMissingItemsData = {
  itemsWithMissingData?: ItemsWithMissingData;
  isEnrolled: boolean;
  verificationEditingRequests: VerificationEditingRequest[];
};

export type FieldWithEnrollmentData = TimelineGroupBase & { data: FieldListField & EnrollmentAndMissingItemsData };

type GroupRendererProps = ReactCalendarGroupRendererProps<FieldWithEnrollmentData> & {
  carbonProgramOutcomes: CarbonProgramResult;
  cropRecommendations: CategorizedFieldCropRecommendation[];
  setFieldsInView: Dispatch<React.SetStateAction<string[]>>;
  onShowMissingDataYear: (fieldId: string, itemId: string) => void;
  carbonProgram: CarbonProgram | null;
  handleClearFilters: () => void;
  itemsWithMissingData: ItemsWithMissingData;
  reportingYear: number;
  shouldShowPayout: boolean;
  onFieldNavigate?: () => void;
  allFieldsPracticesByFieldId?: Record<string, FieldPracticeResponse>;
};

export type CropCardRefMap = Map<string, HTMLDivElement | null>;

const cropRenderer = ({
  carbonProgram,
  item: crop,
  getItemProps,
  itemContext,
  getCropCardRefMap,
  cropCardRefCallback,
  selectedCropIds,
  canMultiSelectCrop,
  itemsWithMissingData,
  reportingYear,
}: {
  carbonProgram?: CarbonProgram;
  item: TimelineCalendarItemsProps<CropItemData>;
  getItemProps: unknown;
  itemContext: unknown;
  getCropCardRefMap: () => CropCardRefMap;
  carbonProgramOutcomesResponse: CarbonProgramOutcomesResponse | undefined;
  cropCardRefCallback: (getCropCardRefMap: () => CropCardRefMap) => (node: HTMLDivElement | null, fieldId: string, itemId: string) => void;
  selectedCropIds: string[];
  selectionConstraints: { cropType: CROP_TYPE_FCPO | null; year: number | null };
  canMultiSelectCrop: (crop: TimelineCalendarItemsProps<CropItemData>) => boolean;
  handleCropSelection: (cropId: string, isSelected: boolean) => void;
  itemsWithMissingData: ItemsWithMissingData;
  reportingYear: number;
} & ReactCalendarItemRendererProps<TimelineCalendarItemsProps<CropItemData>>): React.ReactNode => {
  const cropReferenceDate = crop.data.cropType === CROP_TYPE_FCPO.MAIN ? crop.end_time : crop.start_time;
  const cropIsWithinReportingPeriod = timeIsWithinReportingPeriod(cropReferenceDate.valueOf(), reportingYear, carbonProgram);

  if (crop?.data) {
    const { key, ...rest } = getItemProps(crop.itemProps ?? {});
    const setCropCardRef = cropCardRefCallback(getCropCardRefMap);
    const isCurrentlySelected = !!selectedCropIds.find((cropId) => cropId === crop.id.toString());
    const isSelectableInMultiMode = canMultiSelectCrop(crop);

    const isInMultiSelectionMode = selectedCropIds.length > 0;

    // If in selection mode, show checkboxes for cards matching selection constraints
    // const shouldShowCheckbox =
    //   !isInMultiSelectionMode || (crop.data?.cropType === selectionConstraints.cropType && crop.data?.year === selectionConstraints.year);

    // Determine if the card should be in inactive state during multi selection mode
    const isInactive = isInMultiSelectionMode && !isSelectableInMultiMode && !isCurrentlySelected;

    const isSelectable = isInMultiSelectionMode && isSelectableInMultiMode;

    const hasPendingCropVerification = crop.data?.isFlaggedByVerifier && crop.data?.isMandatory;

    const cropOpenTasksCount = hasPendingCropVerification ? 1 : getCropOpenTasksCount(crop.id.toString(), itemsWithMissingData);

    return (
      <div {...rest} key={key} ref={(node) => setCropCardRef(node, crop.group as string, crop.id.toString())}>
        <CropRotationCard
          isFlaggedByVerifier={!!crop.data?.isFlaggedByVerifier}
          cardViewType={TIMELINE_VIEW.YEARLY_CROP_CARD}
          dataTestId={crop.id.toString()}
          cropId={crop.id.toString()}
          cropName={crop.data?.cropName}
          fieldId={crop.data?.fieldId}
          hasResidue={!!crop.data?.hasResidue}
          cropType={crop.data?.cropType}
          payout={crop.data?.payout}
          currency={carbonProgram?.currency}
          isSelected={itemContext.selected && !isInactive}
          activePracticeBundles={crop.data?.cropPractices?.activePracticeBundles?.filter((bundle) => bundle.active)}
          isMandatory={(crop.data?.cropType === CROP_TYPE_FCPO.MAIN && crop.data?.isMandatory) || isSelectable || hasPendingCropVerification}
          isEmptyCard={crop.isPlaceholder && !crop.data?.cropId && !crop.data?.recommendations}
          isEmptyWithNoEnrollment={crop.data?.cropName === "" && !crop.id.toString().includes("enrolled")}
          recommendations={crop.data?.recommendations}
          enrolledInInventoryProgram={carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory}
          isReportingYear={cropIsWithinReportingPeriod}
          cropData={crop}
          cropMixId={crop.data?.cropMixId}
          isChecked={isCurrentlySelected}
          isInactive={isInactive}
          isConfirmed={!!crop.data?.isConfirmed}
          year={crop.end_time.year()}
          openTasksCount={cropOpenTasksCount}
        />
      </div>
    );
  }

  return <></>;
};

const GroupRenderer = ({
  carbonProgramOutcomes,
  group,
  setFieldsInView,
  onShowMissingDataYear,
  carbonProgram,
  handleClearFilters,
  itemsWithMissingData,
  onFieldNavigate,
  reportingYear,
  shouldShowPayout,
}: GroupRendererProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation("allFieldView");

  const { isSubmissionFeatureEnabled } = useSubmissionStatus();

  const { ref, inView } = useInView({
    delay: 300,
    rootMargin: "48px",
    threshold: 0,
    trackVisibility: true,
    triggerOnce: false,
  });

  useEffect(() => {
    if (group.id === "no-matches") return;

    if (inView) {
      setFieldsInView((prev) => Array.from(new Set([...prev, group.data.id])));
    } else {
      setFieldsInView((prev) => prev.filter((id) => id !== group.data.id));
    }
  }, [inView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle filler group (no matches message)
  if (group.id === "no-matches") {
    return (
      <Flex className="padding-200" vertical align="center" justify="center" gap="1rem">
        <Icon iconName="tabler/search-no-results" size="xxxl" color="#6D6D6D" />
        <Flex vertical align="center" justify="center" gap="0.5rem">
          <p className="margin-0" style={{ textAlign: "center" }}>
            {t("fieldFilterOptions.noMatchesFound")}
          </p>
          <Button type="text" onClick={handleClearFilters}>
            {t("fieldFilterOptions.clearFilters")}
          </Button>
        </Flex>
      </Flex>
    );
  }

  const fieldOutcomes = (carbonProgramOutcomes as SingleFieldOutcomes)?.fieldOutcomes;

  const outcomesAreValid =
    !hasItemsWithMissingData(group.data.itemsWithMissingData) &&
    areFieldOutcomesValidForYear(fieldOutcomes, reportingYear) &&
    (shouldShowNextYearCropsFeatureFlag() ? isNextYearOutcomeValid(fieldOutcomes, reportingYear, carbonProgram) : true);

  const currentAvgPayouts = calculateAvgPayoutForYear(fieldOutcomes, reportingYear);
  const openTasksCount = getFieldOpenTasksCount(itemsWithMissingData, group.data.verificationEditingRequests);
  const hasFieldOpenTasks = hasFieldLevelVerificationEditingRequests(group.data.id, group.data.verificationEditingRequests);

  const handleMissingDataClick = () => {
    if (hasItemsWithMissingData(group.data.itemsWithMissingData)) {
      const sortedItemsWithMissingDataList = getItemsWithMissingDataSortedByStartTime(group.data.itemsWithMissingData);
      const firstItemWithMissingDataId = sortedItemsWithMissingDataList[0]?.id;
      if (firstItemWithMissingDataId) onShowMissingDataYear(group.data.id, firstItemWithMissingDataId);
    }
  };

  const handleFieldCellClick = () => {
    if (!carbonProgram) {
      return;
    }

    // save scroll position before navigate
    onFieldNavigate?.();

    navigate(`/all-fields/field/${group.data.id}`);
  };

  return (
    <>
      {inView ? (
        <div
          ref={ref}
          className="width-100"
          // TODO: remove auto height when submission feature flag is removed
          style={{ height: isSubmissionFeatureEnabled ? "100%" : "auto" }}
        >
          <FieldCell
            fieldName={group.data.name}
            fieldId={group.data.id}
            hasLocation={!!group.data.location}
            isRedZone={group.data.isInRedzone}
            size={group.data.size}
            onClick={handleFieldCellClick}
            onMissingDataClick={handleMissingDataClick}
            hasFieldOutcomes={!!fieldOutcomes && !carbonProgram?.hidePayouts}
            areOutcomesValid={outcomesAreValid}
            currentAvgPayouts={currentAvgPayouts}
            isEnrolled={group.data.isEnrolled}
            openTasksCount={openTasksCount}
            hasFieldOpenTasks={hasFieldOpenTasks}
            shouldShowPayout={shouldShowPayout}
          />
        </div>
      ) : (
        <div ref={ref} className="width-100" style={{ height: "125px" }} />
      )}
    </>
  );
};

const defaultMultiSelectionConstraints: { cropType: CROP_TYPE_FCPO | null; year: number | null } = {
  cropType: null,
  year: null,
};
export const AllFieldsTimeline = ({
  allFields,
  filteredFields,
  allFieldsPractices,
  itemsWithMissingDataByFieldId,
  timelineItems,
  setSimulationFieldId,
  showSimulationFieldSelectionModal,
  setShowSimulations,
  carbonProgramOutcomesResponse,
  isLoadingCarbonProgramOutcomes,
  fieldsInView,
  setFieldsInView,
  cropRecommendationsByFieldId,
  farmFieldsCount,
  hasAppliedFilters,
  handleClearFilters,
  onFieldNavigate,
  verificationEditingRequests,
  reportingYear,
  shouldShowIndividualFieldPayouts,
}: AllFieldsTimelineProps) => {
  const { t } = useTranslation(["allFieldView", "fieldTimeline"]);
  const { smallDesktop, largeDesktop } = useAppType();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentFarm } = useGetFarms();

  const { cmcMixCropFCPO, showDrawerFromCMC, setCMCMixCropFCPO, setSelectedRecommendationItemOnTimeline, setShowDrawerFromCMC } = useCropMixComposerContext();

  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [showBulkEditDrawer, setShowBulkEditDrawer] = useState<boolean>(false);
  const [selectedCropId, setSelectedCropId] = useState<string | null>(null);
  const [selectedCropType, setSelectedCropType] = useState<CROP_TYPE_FCPO>(CROP_TYPE_FCPO.MAIN);
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
  const [selectedCrop, setSelectedCrop] = useState<TimelineCalendarItemsProps<CropItemData> | null>(null);
  const [multiSelectedItems, setMultiSelectedItems] = useState<Map<string, TimelineCalendarItemsProps<CropItemData>>>(new Map());

  const [multiSelectionConstraints, setMultiSelectionConstraints] = useState<{ cropType: CROP_TYPE_FCPO | null; year: number | null }>(
    defaultMultiSelectionConstraints
  );

  const farmId = getFarmIdFromLocalStorage();
  const { data: carbonProgram } = useGetCarbonProgramByFarmId(farmId);

  const { scrollRef, getCropCardRefMap, handleShowMissingDataYear, cropCardRefCallback } = useMissingDataNavigation();

  const isInventoryProgram = carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;

  const mapFields = (field: FieldListField): FieldWithEnrollmentData => {
    const isEnrolled = isFieldEnrolled(carbonProgramOutcomesResponse?.[field.id]);

    return {
      id: field.id,
      title: field.id,
      height: 100,
      data: { ...field, isEnrolled, verificationEditingRequests: verificationEditingRequests?.filter((request) => request.fieldId === field.id) ?? [] },
    };
  };

  const sortFields = (a: FieldWithEnrollmentData, b: FieldWithEnrollmentData) => {
    if (isInventoryProgram) {
      return b.data.size - a.data.size;
    }

    if (a.data.isEnrolled !== b.data.isEnrolled) {
      return a.data.isEnrolled ? -1 : 1;
    }
    return b.data.size - a.data.size;
  };

  // Create a stable reference to track editing requests changes
  const editingRequestsSnapshot = useMemo(
    () =>
      verificationEditingRequests
        ?.map((request) => ({ id: request.id, fieldId: request.fieldId, completedAt: request.completedAt }))
        .sort((a, b) => a.id.localeCompare(b.id))
        .join(",") ?? "",
    [verificationEditingRequests]
  );

  // Create a stable snapshot of missing data to prevent unnecessary re-renders
  const missingDataSnapshot = useMemo(() => {
    if (!itemsWithMissingDataByFieldId) return "";

    return Object.entries(itemsWithMissingDataByFieldId)
      .map(([fieldId, data]) => {
        if (!data) return `${fieldId}:empty`;

        const counts = Object.values(data).reduce((acc, curr) => (typeof curr === "boolean" ? acc + (curr ? 1 : 0) : acc + curr.length), 0);
        return `${fieldId}:${counts}`;
      })
      .sort()
      .join("|");
  }, [itemsWithMissingDataByFieldId]);

  const memoizedFields: FieldWithEnrollmentData[] = useMemo(
    () => filteredFields?.map(mapFields).sort(sortFields),
    [filteredFields, carbonProgramOutcomesResponse, editingRequestsSnapshot, missingDataSnapshot, shouldShowIndividualFieldPayouts] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const itemsToDisplay = useMemo(() => timelineItems.filter((item) => fieldsInView.includes(item.group as string)), [timelineItems, fieldsInView]);

  // Filter out non-selectable placeholders when in multiselection mode
  const filteredItemsToDisplay = useMemo(() => {
    if (multiSelectedItems.size === 0 || multiSelectionConstraints.cropType === null || multiSelectionConstraints.year === null) return itemsToDisplay;

    const items = Array.from(multiSelectedItems.entries());
    const selectedExistingCrops = items.filter(([_, crop]) => !crop.isPlaceholder).length;

    return itemsToDisplay.filter((item) => {
      const isSelected = multiSelectedItems.has(item.id as string);
      if (isSelected) return true;
      if (selectedExistingCrops > 1 && item.isPlaceholder) return false;
      if (!item.isPlaceholder) return true;
      // Show placeholders that match the current multiselection constraints
      return item.data?.cropType === multiSelectionConstraints.cropType && item.data?.year === multiSelectionConstraints.year;
    });
  }, [itemsToDisplay, multiSelectionConstraints, multiSelectedItems]);

  const fieldsWithMissingDataId = memoizedFields.map((field) => {
    const itemsWithMissingData = itemsWithMissingDataByFieldId[field.id];
    if (!itemsWithMissingData || itemsWithMissingData.hasErrors) return field;

    field.data.itemsWithMissingData = itemsWithMissingData;
    return field;
  });

  // Add filler group when no matches are found and search/filters are active
  const groupsWithFiller = useMemo(() => {
    if (!hasAppliedFilters || (hasAppliedFilters && fieldsWithMissingDataId.length > 0)) return fieldsWithMissingDataId;

    const fillerGroup: FieldWithEnrollmentData = {
      id: "no-matches",
      title: "",
      height: 600,
      data: {
        id: "no-matches",
        name: "",
        size: 0,
        isInRedzone: false,
        redzones: [],
        location: { lat: 0, lng: 0 },
        regionId: "",
        isEnrolled: false,
        itemsWithMissingData: undefined,
        verificationEditingRequests: [],
      },
    };

    return [...fieldsWithMissingDataId, fillerGroup];
  }, [fieldsWithMissingDataId, hasAppliedFilters]);

  const isFetchingHook = !!useIsFetching({ queryKey: [GET_FIELD_PRACTICES] });
  const isMutatingHook = !!useIsMutating();

  const isCMCIconClick = (target: HTMLElement | null) => target?.classList.contains("cmc-plant-icon");

  const getYear = (cropItem: TimelineCalendarItemsProps<CropItemData>): number => {
    console.assert(cropItem.data?.year, "A crop item should always have a year!"); // eslint-disable-line
    return cropItem.data?.year!; // eslint-disable-line
  };

  const canMultiSelectCrop = useCallback(
    (crop: TimelineCalendarItemsProps<CropItemData>): boolean => {
      if (multiSelectionConstraints.cropType === null && multiSelectionConstraints.year === null) {
        return true; // First selection
      }

      // Only allow selecting placeholders if only one existing crop is selected
      // Do not allow selecting more existing crops if one existing crop and at least one placeholder are selected
      const selectedItems = Array.from(multiSelectedItems.entries());
      const selectedExistingCrops = selectedItems.filter(([_, crop]) => !crop.isPlaceholder).length;
      const selectedPlaceholderCrops = selectedItems.filter(([_, crop]) => crop.isPlaceholder).length;
      const isSelected = multiSelectedItems.has(crop.id as string);
      if (isSelected) return true;
      if (selectedExistingCrops > 1 && crop.isPlaceholder) return false;
      if (selectedExistingCrops === 1 && selectedPlaceholderCrops > 0) {
        if (!crop.isPlaceholder) return false;
      }

      return crop.data?.cropType === multiSelectionConstraints.cropType && crop.data?.year === multiSelectionConstraints.year;
    },
    [multiSelectionConstraints.cropType, multiSelectionConstraints.year, multiSelectedItems]
  );

  const handleCropMultiSelection = useCallback(
    (cropId: string, isSelected: boolean) => {
      const timeLineItem = filteredItemsToDisplay.find((item) => item.id === cropId);
      if (!timeLineItem) return;

      if (isSelected) {
        // Add to selection
        if (multiSelectedItems.size === 0) {
          // First selection - set constraints
          setMultiSelectionConstraints({
            cropType: timeLineItem.data?.cropType || defaultMultiSelectionConstraints.cropType,
            year: timeLineItem.data?.year || defaultMultiSelectionConstraints.year,
          });
        }

        if (canMultiSelectCrop(timeLineItem)) {
          setMultiSelectedItems((prev) => new Map(prev).set(cropId, timeLineItem));
        }
      } else {
        // Remove from selection
        setMultiSelectedItems((prev) => {
          const newMap = new Map(prev);
          newMap.delete(cropId);

          // Reset constraints if no crops selected
          if (newMap.size === 0) {
            setMultiSelectionConstraints(defaultMultiSelectionConstraints);
          }
          return newMap;
        });
      }
    },
    [filteredItemsToDisplay, multiSelectedItems, canMultiSelectCrop, setMultiSelectionConstraints, setMultiSelectedItems]
  );

  const handleMultiSelectMode = useCallback(
    (cropId: string, foundCrop: TimelineCalendarItemsProps<CropItemData>, isCheckboxClick: boolean): boolean => {
      const isMultiMode = multiSelectedItems.size > 0;
      const isSelectableInMultiMode = canMultiSelectCrop(foundCrop);

      if (!isMultiMode && isCheckboxClick) {
        const isChecked = multiSelectedItems.has(cropId);
        handleCropMultiSelection(cropId, !isChecked);
        return true;
      }

      if (isMultiMode && isSelectableInMultiMode) {
        const isChecked = multiSelectedItems.has(cropId);
        handleCropMultiSelection(cropId, !isChecked);
        return true;
      }

      if (isMultiMode && !isSelectableInMultiMode) return true;

      return false; // not handled, continue with regular click
    },
    [multiSelectedItems, canMultiSelectCrop, handleCropMultiSelection]
  );

  const handleCardClick = useCallback(
    (cropId: string, event?: React.SyntheticEvent) => {
      const foundCrop = filteredItemsToDisplay.find((field) => field.id === cropId);
      if (!foundCrop) return;
      if (selectedCropId === cropId && (isFetchingHook || isMutatingHook)) return;

      // Handle multi-select mode first
      const target = event?.target as HTMLElement;
      const isCheckbox = isCheckboxClick(target);
      const wasHandledByMultiSelect = handleMultiSelectMode(cropId, foundCrop, isCheckbox);
      if (wasHandledByMultiSelect) return;

      // Extract the base path before /cmc to avoid double /cmc paths
      const basePath = location.pathname.includes("/cmc") ? location.pathname.split("/cmc")[0].replace(/\/$/, "") : location.pathname.replace(/\/$/, "");

      // Continue with a regular click
      if (foundCrop.practiceType === PRACTICE_TYPE.SIDE_CROP_RECOMMENDATION) {
        if (foundCrop.data.cropType === CROP_TYPE_FCPO.UNDERSOWING) return;
        setShowDrawer(false);
        setSelectedCropId(cropId);
        setSelectedCropType(foundCrop.data.cropType);
        setActiveFieldId(foundCrop.group as string);
        setSelectedCrop(foundCrop);
        setSelectedRecommendationItemOnTimeline(foundCrop);
        navigate(`${basePath}/cmc${location.search}`);
        return;
      }

      if (isCMCIconClick(event?.target as HTMLElement) || isCMCIconClick((event?.target as HTMLElement)?.parentElement)) {
        setShowDrawer(false);
        setSelectedCropId(cropId);
        setSelectedCropType(foundCrop.data.cropType);
        setActiveFieldId(foundCrop.group as string);
        setSelectedCrop(foundCrop);
        setSelectedRecommendationItemOnTimeline?.(foundCrop);
        navigate(`${basePath}/cmc/${foundCrop.data.cropMixId}${location.search}`);
        return;
      }

      setSelectedRecommendationItemOnTimeline?.(foundCrop);
      setCMCMixCropFCPO({} as CropFCPO);
      setSelectedCropId(cropId);
      setSelectedCropType(foundCrop.data.cropType);
      setActiveFieldId(foundCrop.group as string);
      setSelectedCrop(foundCrop);
      setShowDrawer(true);
    },
    [
      navigate,
      location,
      isFetchingHook,
      isMutatingHook,
      filteredItemsToDisplay,
      selectedCropId,
      setSelectedRecommendationItemOnTimeline,
      setShowDrawer,
      setSelectedCrop,
      setSelectedCropType,
      setActiveFieldId,
      setCMCMixCropFCPO,
      handleMultiSelectMode,
    ]
  );

  const existingCrops = !activeFieldId ? [] : allFieldsPractices[activeFieldId]?.crops ?? [];

  const getAllCropsWithFieldIdsBasedOnMultiSelection = () => {
    const cropsByFieldId: {
      [fieldId: string]: FieldPracticeResponse;
    } = {};
    const affectedFieldIds = (Array.from(new Set(selectedCropsData.map((crop) => allFields.find((f) => f.id === crop.fieldId)))) as FieldListField[]).map(
      (field) => field.id
    );
    if (!affectedFieldIds) return null;
    for (const id of affectedFieldIds) {
      const value = allFieldsPractices[id];
      if (value) {
        cropsByFieldId[id] = value;
      }
    }
    return cropsByFieldId;
  };

  const existingFertilizers =
    selectedCrop?.data?.cropPractices?.practices?.filter(
      (practice) =>
        (practice.type === PRACTICE_SLUG.MINERAL_FERTILIZERS || practice.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS) &&
        (selectedCrop?.id.toString().includes("UNKNOWN") || practice.cropId === selectedCrop?.id)
    ) ?? [];

  const existingTillages =
    selectedCrop?.data?.cropPractices?.practices?.filter(
      (practice) => practice.type === PRACTICE_SLUG.TILLAGES && (selectedCrop?.id.toString().includes("UNKNOWN") || practice.cropId === selectedCrop?.id)
    ) ?? [];

  const handleBulkEdit = () => {
    if (multiSelectedItems.size === 0) return;
    setShowBulkEditDrawer(true);
  };

  const handleClearSelection = () => {
    setMultiSelectedItems(new Map());
    setMultiSelectionConstraints(defaultMultiSelectionConstraints);
  };

  const handleBulkEditFinished = () => {
    setMultiSelectedItems(new Map());
    setMultiSelectionConstraints(defaultMultiSelectionConstraints);
    setShowBulkEditDrawer(false);
  };

  // Get selected crops data for bulk editing
  const getSelectedCropsData = (): { selectedCropsData: BulkEditCrop[]; selectedTillages: PracticeFCPO[]; selectedFertilizers: PracticeFCPO[] } => {
    const selectedCrops: BulkEditCrop[] = [];
    const selectedTillages: PracticeFCPO[] = [];
    const selectedFertilizers: PracticeFCPO[] = [];

    multiSelectedItems.forEach((timelineItem) => {
      if (timelineItem?.data) {
        const fieldId = timelineItem.group as string;
        const field = allFields.find((f) => f.id === fieldId);
        const selectedTimelineItems = timelineItem.data?.cropPractices?.crops?.find((crop) => crop.id === timelineItem.id);
        const { cropPractices } = timelineItem.data;
        const availablePracticeBundles = cropPractices?.activePracticeBundles;
        if (field) {
          let cropData: BulkEditCrop;
          const tillages: PracticeFCPO[] = [];
          const fertilizers: PracticeFCPO[] = [];
          if (selectedTimelineItems) {
            // Convert timeline item data to CropFCPO format
            cropData = {
              ...selectedTimelineItems,
              // Add other required fields with defaults
              fieldId,
              isInRedzone: field.isInRedzone,
              isMandatory: timelineItem.data.isMandatory,
              isBaseline: timelineItem.data.isBaseline,
              isPlaceholder: timelineItem.isPlaceholder,
              availablePracticeBundles: availablePracticeBundles || [],
              fertilizerManagement: timelineItem.data.fertilizerManagements,
            };
            const foundTillages = cropPractices?.practices?.filter((practice) => practice.type === "tillages");
            if (foundTillages) {
              tillages.push(...foundTillages);
            }
            const foundFertilizers = cropPractices?.practices?.filter(
              (practice) => practice.type === "mineralFertilizers" || practice.type === "organicFertilizers"
            );
            if (foundFertilizers) {
              fertilizers.push(...foundFertilizers);
            }
          } else {
            cropData = {
              id: timelineItem.id.toString(),
              fieldId: fieldId,
              cropId: timelineItem.data.cropId || "",
              cropName: timelineItem.data.cropName || "",
              cropType: timelineItem.data.cropType,
              year: timelineItem.data.year,
              seedingStartDate: timelineItem.start_time.toISOString(),
              seedingEndDate: "",
              harvestStartDate: "",
              harvestEndDate: timelineItem.end_time.toISOString(),
              hasResidue: timelineItem.data.hasResidue,
              isConfirmed: timelineItem.data.isConfirmed,
              yieldTonsPerHa: timelineItem.data.yieldTonsPerHa,
              payout: timelineItem.data.payout || 0,
              translationSlug: "",
              // Add other required fields with defaults
              seedingPracticeType: "",
              isMandatory: timelineItem.data.isMandatory,
              isBaseline: timelineItem.data.isBaseline,
              isPlaceholder: timelineItem.isPlaceholder,
              availablePracticeBundles: availablePracticeBundles || [],
              fertilizerManagement: timelineItem.data.fertilizerManagements,
              seedingVerificationStatus: null,
              isInRedzone: field.isInRedzone,
            };
          }

          selectedCrops.push(cropData);
          selectedTillages.push(...tillages);
          selectedFertilizers.push(...fertilizers);
        }
      }
    });

    return { selectedCropsData: selectedCrops, selectedTillages, selectedFertilizers };
  };

  const { selectedCropsData, selectedTillages, selectedFertilizers } = getSelectedCropsData();

  const { isSubmissionFeatureEnabled } = useSubmissionStatus();

  return (
    <>
      <ModalProvider>
        <TimelineCalendarWithFields
          groups={groupsWithFiller}
          allFields={filteredFields}
          onItemClick={(itemId: string, e: React.SyntheticEvent) => handleCardClick(itemId, e)}
          onItemSelect={(itemId: string, e: React.SyntheticEvent) => handleCardClick(itemId, e)}
          // @ts-expect-error nope should be a date type and not a number
          items={filteredItemsToDisplay}
          groupRenderer={({ group }) =>
            isLoadingCarbonProgramOutcomes ? (
              <Flex align="center" justify="center" style={{ height: "100px" }}>
                <Spin />
              </Flex>
            ) : (
              GroupRenderer({
                carbonProgram: carbonProgram ?? null,
                carbonProgramOutcomes: carbonProgramOutcomesResponse?.[group.id] as SingleFieldOutcomes,
                cropRecommendations: cropRecommendationsByFieldId?.[group.id] ?? [],
                group,
                setFieldsInView,
                onShowMissingDataYear: handleShowMissingDataYear,
                handleClearFilters,
                itemsWithMissingData: itemsWithMissingDataByFieldId?.[group.id] ?? {},
                onFieldNavigate,
                reportingYear,
                shouldShowPayout: shouldShowIndividualFieldPayouts,
              })
            )
          }
          itemRenderer={({ item, getItemProps, itemContext, ...props }) =>
            cropRenderer({
              item: item as unknown as TimelineCalendarItemsProps<CropItemData>,
              getItemProps,
              itemContext,
              getCropCardRefMap,
              carbonProgram,
              carbonProgramOutcomesResponse,
              cropCardRefCallback,
              selectedCropIds: multiSelectedItems.keys().toArray(),
              selectionConstraints: multiSelectionConstraints,
              canMultiSelectCrop,
              handleCropSelection: handleCropMultiSelection,
              itemsWithMissingData: itemsWithMissingDataByFieldId?.[item.group as string] ?? {},
              reportingYear,
              ...props,
            })
          }
          highlightCurrentYear={true}
          tagText={t("reportingYear")}
          dragSnap={dayInMilliseconds}
          canChangeGroup={false}
          canMove={true}
          canResize={true}
          scrollRef={(el) => (scrollRef.current = el)}
          buffer={10}
          screenSize={largeDesktop ? "large" : smallDesktop ? "small" : "medium"}
          farmFieldsCount={farmFieldsCount}
          hasAppliedFilters={hasAppliedFilters}
          carbonProgramOutcomesResponse={carbonProgramOutcomesResponse}
          carbonProgram={carbonProgram}
          reportingYear={reportingYear}
          // moveResizeValidator={(action, itemId, time, resizeEdge) => moveResizeValidator({ action, item: itemId, time, resizeEdge, currentItems: items })} // 🚨 : I will skip this for now since it is WIP
        />
        {(activeFieldId && showDrawerFromCMC && cmcMixCropFCPO.id && (
          <AddEditCropDrawer
            availablePracticeBundles={allFieldsPractices[activeFieldId]?.availablePracticeBundles ?? []}
            allCrops={existingCrops}
            existingCrop={cmcMixCropFCPO}
            year={cmcMixCropFCPO?.year ?? moment().year()}
            existingFertilizers={existingFertilizers}
            existingTillages={existingTillages}
            field={filteredFields.find((field) => field.id === activeFieldId) ?? filteredFields[0]}
            setShowDrawer={setShowDrawerFromCMC}
            showDrawer={showDrawerFromCMC}
            fertilizerManagements={selectedCrop?.data?.fertilizerManagements ?? []}
            viewType={VIEW_TYPE.ALL_FIELDS}
            farmId={currentFarm?.id}
            carbonProgram={currentFarm?.carbonProgram}
          />
        )) ||
          (selectedCropId && showDrawer && selectedCrop && (
            <AddEditCropDrawer
              availablePracticeBundles={selectedCrop?.data?.cropPractices?.activePracticeBundles ?? []}
              allCrops={existingCrops}
              existingCrop={
                selectedCrop.data?.cropPractices?.crops?.find((crop) => crop.id === selectedCrop.id) ??
                NEW_CROP(selectedCropType, moment(selectedCrop.start_time).toISOString(), moment(selectedCrop.end_time).toISOString())
              }
              year={getYear(selectedCrop)}
              existingFertilizers={existingFertilizers}
              existingTillages={existingTillages}
              field={filteredFields.find((field) => field.id === activeFieldId) ?? filteredFields[0]}
              setShowDrawer={setShowDrawer}
              showDrawer={showDrawer}
              fertilizerManagements={selectedCrop?.data?.fertilizerManagements ?? []}
              viewType={VIEW_TYPE.ALL_FIELDS}
              formWithOnLoadValidation={isSubmissionFeatureEnabled}
              isMandatoryCropCard={selectedCrop?.data?.isMandatory}
              farmId={currentFarm?.id}
              carbonProgram={currentFarm?.carbonProgram}
            />
          ))}

        {/* Bulk Edit Crop Drawer */}
        {showBulkEditDrawer && selectedCropsData.length > 0 && (
          <BulkEditCropDrawer
            selectedCrops={selectedCropsData}
            allCrops={getAllCropsWithFieldIdsBasedOnMultiSelection()}
            affectedFields={Array.from(new Set(selectedCropsData.map((crop) => allFields.find((f) => f.id === crop.fieldId)))) as FieldListField[]}
            selectedTillages={selectedTillages}
            selectedFertilizers={selectedFertilizers}
            showDrawer={showBulkEditDrawer}
            setShowDrawer={setShowBulkEditDrawer}
            onFinish={handleBulkEditFinished}
          />
        )}

        {memoizedFields.length ? (
          <SimulationFieldSelectionModal
            fields={memoizedFields}
            showModal={showSimulationFieldSelectionModal}
            setSimulationFieldId={setSimulationFieldId}
            setShowSimulations={setShowSimulations}
          />
        ) : null}

        {/* Multi-selection floating action bar */}
        {multiSelectedItems.size > 0 && (
          <MultiCropSelectionBar selectedCount={multiSelectedItems.size} onEdit={handleBulkEdit} onClear={handleClearSelection} />
        )}
      </ModalProvider>
    </>
  );
};
